// Selection and URL-restore logic: pure, DOM- and state-free, so it can be
// unit-tested. main.ts binds these to the live state (toggleSplit, the
// exclusive-dim handler, restoreFromUrl).
import { defaultSelection, lineDims, singleSelectDims, splitDimOf, type Combo, type Meta, type Selection } from "./meta";
import { parseDimsStr, parseExtraKey, type Route } from "./route";
import { defaultTransform, transformOptions } from "./transforms";

/** Most comparison series (overlays) on one chart. */
export const MAX_EXTRAS = 5;

/** Toggle one level of a line dim. Added levels go last, so the lines already
 * drawn keep their colours (colour = position in the selection). A dim always
 * keeps at least one level. Returns a new selection. */
export function toggleLevel(sel: Selection, dim: string, code: string): Selection {
  const cur = sel[dim] || [];
  const next = cur.includes(code) ? (cur.length > 1 ? cur.filter(c => c !== code) : cur) : [...cur, code];
  return { ...sel, [dim]: next.slice() };
}

/** Make sure the selection draws at least one line. If the exclusive
 * (single-select) picks leave no data, flip the exclusive dims other than
 * `keepDim` to a combination that has some, preferring one that also matches
 * the line dims' selection. Line dims are never changed: their empty cells
 * simply do not draw. No combinations index = nothing to check against. */
export function autoCorrect(meta: Meta, combos: Combo[] | null | undefined, sel: Selection, keepDim?: string): Selection {
  if (!combos || !combos.length) return sel;
  const ss = singleSelectDims(meta);
  const adjustable = ss.filter(d => d !== keepDim);
  if (!adjustable.length) return sel;
  const matchOn = (dims: string[]) => (t: Combo) =>
    dims.every(d => { const s = sel[d]; return !s || !s.length || s.includes(t[d] as string); });
  const matchSS = matchOn(ss), matchLine = matchOn(lineDims(meta));
  if (combos.some(t => matchSS(t) && matchLine(t))) return sel;
  const keepOk = (t: Combo) => !keepDim || (sel[keepDim] || []).includes(t[keepDim] as string);
  const cand = combos.find(t => keepOk(t) && matchLine(t)) || combos.find(keepOk);
  if (!cand) return sel;
  const out = { ...sel };
  for (const d of adjustable) out[d] = [cand[d] as string];
  return out;
}

/** Pick one level of an exclusive dim, then auto-correct the others. */
export function pickExclusive(meta: Meta, combos: Combo[] | null | undefined, sel: Selection, dim: string, code: string): Selection {
  return autoCorrect(meta, combos, { ...sel, [dim]: [code] }, dim);
}

/** What a shared URL asks the chart to show. `explicit` is true when the URL
 * named a selection (dims= or series=), false for the dataset's default. */
export interface View {
  selection: Selection;
  explicit: boolean;
  transform: string;
  /** Overlay keys, "ds:<id>" / "sub:<id>:<code>". */
  extras: string[];
  title: string | null;
  /** Visible window as raw period-start epoch seconds [min, max]. */
  window: [number, number] | null;
  /** A range preset in years from r=5y, or null. */
  preset: string | null;
}

/** Read a route (lib/route parseRoute) into a View, keeping only what can be
 * drawn: dims the dataset has, a transform its frequency offers, overlays of
 * datasets in the catalog (`knownIds`), a window with two dates in order.
 * Hand-edited or outdated links then still open a working chart. */
export function viewFromRoute(r: Route, meta: Meta, knownIds: Set<string>): View {
  const selection = defaultSelection(meta);
  let explicit = false;
  // Only codes the dataset has: a renamed code (ch_ffa_finances "Rechnung" ->
  // "rechnung") would otherwise go to the API and draw nothing.
  const known = (d: string, codes: string[]) => {
    const levels = meta.dimensions && meta.dimensions[d] && meta.dimensions[d].levels;
    return levels ? codes.filter(c => Object.prototype.hasOwnProperty.call(levels, c)) : [];
  };
  if (r.dims) {
    const asked = parseDimsStr(r.dims);
    for (const d of Object.keys(asked)) {
      const codes = known(d, asked[d] || []);
      if (codes.length) { selection[d] = codes; explicit = true; }
    }
  } else if (r.series) {
    const split = splitDimOf(meta);
    const codes = split ? known(split, r.series.split(",").filter(Boolean)) : [];
    if (split && codes.length) { selection[split] = codes; explicit = true; }
  }
  const offered = transformOptions(meta.frequency).map(([v]) => v as string);
  const transform = r.t && offered.includes(r.t) ? r.t : defaultTransform(meta.frequency, meta.default_transform);
  const extras = (r.add || "").split(",").filter(k => {
    const ref = parseExtraKey(k);
    return !!ref && !!ref.datasetId && knownIds.has(ref.datasetId) && (ref.kind === "ds" || !!ref.code);
  }).slice(0, MAX_EXTRAS);
  let window: [number, number] | null = null;
  const pm = (r.r || "").match(/^(\d+)y$/);
  const preset = pm ? pm[1] as string : null;
  if (r.r && !preset) {
    const [a, b] = r.r.split(",").map(x => Date.parse(x) / 1000);
    if (a != null && b != null && Number.isFinite(a) && Number.isFinite(b) && a < b) window = [a, b];
  }
  return { selection, explicit, transform, extras, title: r.title || null, window, preset };
}
